import { debugLog, isDebugMode } from '../utils/debug.js'
import * as THREE from 'three'

const basicLerp = (start, end, t) => start * (1 - t) + end * t

export class CameraManager
{
  // Pre-allocated reusable objects to avoid GC pressure during scroll
  _slerpTempMatrix = new THREE.Matrix4()
  _slerpTempUp = new THREE.Vector3(0, 1, 0)
  _slerpTempForward = new THREE.Vector3()
  _slerpQStart = new THREE.Quaternion()
  _slerpQEnd = new THREE.Quaternion()
  _slerpQResult = new THREE.Quaternion()
  _slerpInterpolatedPos = new THREE.Vector3()
  _slerpInterpolatedTarget = new THREE.Vector3()

  constructor(sceneManager, controls, camera, scroll = null)
  {
    this.scroll = scroll
    this.sceneManager = sceneManager
    this.controls = controls
    this.camera = camera
    this.busy = false
    this.hasUserScrolled = false
    this.scrollOffset = 0
    this.currentWaypoint = null
    this.needsAdjustedPositionUpdate = true

    // Bind resize handler
    this._resizeTimeout = null
    this._handleResize = this._handleResize.bind(this)
    window.addEventListener('resize', this._handleResize)

    // Bind scroll listener
    this._handleScroll = this._handleScroll.bind(this)

    // Set up scroll listener
    if (this.scroll && this.scroll.el)
    {
      this.scroll.el.addEventListener('scroll', this._handleScroll)
    }

    // Initialize camera to first waypoint
    this.goToSceneZoneByIndex(0)
    // Delay initial calculation to allow layout to settle
    setTimeout(() => this.recalculateAdjustedPositions(), 100)
  }

  /**
   * Internal scroll event handler
   */
  _handleScroll()
  {
    if (this.busy || !this.scroll || !this.scroll.el) return
    this.hasUserScrolled = true
    this.scrollOffset = this.scroll.el.scrollTop / this.scroll.el.scrollHeight
  }

  /**
   * Update scroll object reference (Drei ScrollControls may recreate state on resize).
   */
  setScroll(scroll)
  {
    if (this.scroll !== scroll)
    {
      // Remove listener from old element
      if (this.scroll && this.scroll.el)
      {
        this.scroll.el.removeEventListener('scroll', this._handleScroll)
      }

      this.scroll = scroll

      // Add listener to new element
      if (this.scroll && this.scroll.el)
      {
        this.scroll.el.addEventListener('scroll', this._handleScroll)
        // Force update initial offset
        this._handleScroll()
      }
    }
  }

  /**
   * Set scroll position to a percentage of total scrollable height.
   */
  setScrollPercentage(element, percentage)
  {
    const totalHeight = element.scrollHeight - element.clientHeight
    element.scrollTop = percentage * totalHeight
  }

  /**
   * Navigate to a scene zone by array index.
   */
  goToSceneZoneByIndex(index, block = true)
  {
    if (block && this.busy) return


    // Validate index range
    if (index < 0 || index >= this.sceneManager.waypoints.length) return

    this.busy = true

    const sceneZone = this.sceneManager.waypoints[ index ]
    if (!sceneZone)
    {
      debugLog('[CameraManager] Scene zone not found, index:', index)
      this.busy = false
      return
    }

    this.goToSceneZone(sceneZone)
    this.setCurrentWaypoint(sceneZone)
    if (!block) this.busy = false
  }

  /**
   * Navigate to a scene zone by name.
   */
  goToSceneZoneByName(name)
  {
    if (this.busy || !this.camera || !this.sceneManager) return
    this.busy = true

    const sceneZone = this.sceneManager.getSceneZone(name)
    if (!sceneZone)
    {
      debugLog('[CameraManager] Scene zone not found:', name)
      this.busy = false
      return
    }

    this.goToSceneZone(sceneZone)
    this.setCurrentWaypoint(sceneZone)
  }

  /**
   * Smoothly navigate camera to a scene zone.
   */
  goToSceneZone(sceneZone)
  {
    if (!sceneZone) return
    if (!this.controls) return

    this.hasUserScrolled = false

    // Update scroll position
    if (this.scroll)
    {
      const waypointArrayIndex = this.sceneManager.waypoints.indexOf(sceneZone)
      const waypointCount = this.sceneManager.waypoints.length
      const scrollTargetOffset = waypointCount > 1 ? waypointArrayIndex / (waypointCount - 1) : 0
      this.setScrollPercentage(this.scroll.el, scrollTargetOffset)
    }

    // Get camera position
    let position = sceneZone.camera.anchor?.position
    if (!position) return

    const target = sceneZone.camera.targetPosition

    // Apply adjusted position for keepContentInFrame
    position = this.getAdjustedPositionForZone(sceneZone, position, target)

    // Show debug visualization in debug mode
    if (isDebugMode)
    {
      const debugBox = this.calculateZoneBoundingBox(sceneZone)
      this.showDebugBoundingBox(sceneZone, debugBox, true)
    }

    // Update camera properties directly (no animation for now)
    this.updateCameraProperties(sceneZone)

    // Animate camera to new position
    this.controls.setLookAt(...position, ...target, true).then(() =>
    {
      this.busy = false
    })
  }

  /**
   * Get adjusted position for a zone, handling keepContentInFrame.
   */
  getAdjustedPositionForZone(sceneZone, position, target)
  {
    const waypoint = this.sceneManager.waypoints.find(w => w.index === sceneZone.index)
    if (waypoint)
    {
      return this.getAdjustedPosition(waypoint)
    }

    if (!sceneZone.keepContentInFrame) return position

    // Fallback: calculate dynamically
    const targetBox = this.calculateZoneBoundingBox(sceneZone)
    if (!targetBox || targetBox.isEmpty()) return position

    const currentDist = position.distanceTo(target)
    const fittedDist = this.calculateDistanceToFitBox(targetBox, sceneZone.camera.anchor.fov, null, 1.15)

    if (fittedDist > currentDist)
    {
      const direction = new THREE.Vector3().subVectors(position, target).normalize()
      return target.clone().add(direction.multiplyScalar(fittedDist))
    }

    return position
  }

  /**
   * Update camera FOV, near, and far properties directly.
   */
  updateCameraProperties(sceneZone)
  {
    if (sceneZone.camera.anchor.fov) this.controls.camera.fov = sceneZone.camera.anchor.fov
    if (sceneZone.camera.anchor.near) this.controls.camera.near = sceneZone.camera.anchor.near
    if (sceneZone.camera.anchor.far) this.controls.camera.far = sceneZone.camera.anchor.far || 10000

    this.controls.camera.updateProjectionMatrix()
    this.controls.update(0)
  }

  /**
   * Handle scroll-based camera interpolation between waypoints.
   */
  scrollHandler()
  {
    if (!this.scroll || !this.hasUserScrolled || this.busy) return

    const scaledScrollOffset = this.scrollOffset * (this.sceneManager.waypoints.length - 1)
    const currentZoneIndex = Math.floor(scaledScrollOffset)
    const nextZoneIndex = Math.ceil(scaledScrollOffset)
    const currentZone = this.sceneManager.waypoints[ currentZoneIndex ]
    const nextZone = this.sceneManager.waypoints[ nextZoneIndex ]

    if (!currentZone || !nextZone) return

    const percent = scaledScrollOffset % 1
    this.interpolateCameraPosition(currentZone, nextZone, percent)
    this.interpolateCameraProperties(currentZone, nextZone, percent)
  }

  /**
   * Interpolate camera position between two waypoints.
   */
  interpolateCameraPosition(currentZone, nextZone, percent)
  {
    const currentPos = this.getAdjustedPosition(currentZone)
    const nextPos = this.getAdjustedPosition(nextZone)

    // Linear interpolation for position
    this._slerpInterpolatedPos.copy(currentPos).lerp(nextPos, percent)

    // SLERP for rotation using anchor quaternions (Blender-exported)
    this._slerpQStart.copy(currentZone.camera.anchor.quaternion)
    this._slerpQEnd.copy(nextZone.camera.anchor.quaternion)
    this._slerpQResult.copy(this._slerpQStart).slerp(this._slerpQEnd, percent)

    // Interpolate focal distance
    const currentTarget = currentZone.camera.targetPosition
    const nextTarget = nextZone.camera.targetPosition
    const distStart = currentPos.distanceTo(currentTarget)
    const distEnd = nextPos.distanceTo(nextTarget)
    const distResult = basicLerp(distStart, distEnd, percent)

    // Calculate target by projecting forward from position
    this._slerpTempForward.set(0, 0, -1).applyQuaternion(this._slerpQResult)
    this._slerpInterpolatedTarget.copy(this._slerpInterpolatedPos).add(
      this._slerpTempForward.multiplyScalar(distResult)
    )

    // Apply to camera
    this.controls.setLookAt(
      this._slerpInterpolatedPos.x, this._slerpInterpolatedPos.y, this._slerpInterpolatedPos.z,
      this._slerpInterpolatedTarget.x, this._slerpInterpolatedTarget.y, this._slerpInterpolatedTarget.z,
      true
    )
  }

  /**
   * Interpolate camera FOV, near, far between two waypoints.
   */
  interpolateCameraProperties(currentZone, nextZone, percent)
  {
    const fov = basicLerp(currentZone.camera.anchor.fov || 50, nextZone.camera.anchor.fov || 50, percent)
    const near = basicLerp(currentZone.camera.anchor.near || 0.1, nextZone.camera.anchor.near || 0.1, percent)
    const far = basicLerp(currentZone.camera.anchor.far || 10000, nextZone.camera.anchor.far || 10000, percent)

    this.controls.camera.fov = fov
    this.controls.camera.near = near
    this.controls.camera.far = far
    this.controls.camera.updateProjectionMatrix()
    this.controls.update(0)
  }


  update()
  {
    // debugLog('[CameraManager] update', this.scroll?.delta, this.hasUserScrolled)
    let forceUpdate = false

    // If a resize happened, we need to update positions
    // We do this here (pre-frame) instead of in the resize listener to ensure
    // we have the latest camera aspect ratio from the Three.js renderer.
    if (this.needsAdjustedPositionUpdate)
    {
      this.recalculateAdjustedPositions()
      forceUpdate = true
    }

    // Direct check of scroll state (Drei ScrollControls)
    if (this.scroll)
    {
      // Valid delta indicates movement
      if (this.scroll.delta > 0.00001)
      {
        this.hasUserScrolled = true
        this.scrollOffset = this.scroll.offset
        this.scrollHandler()
        this.updateCurrentWaypointFromScroll()
      }
      // If we just resized (forceUpdate) and have scrolled before, re-run handler to apply new bounds
      else if (forceUpdate && this.hasUserScrolled)
      {
        debugLog('[CameraManager] Force updating camera position due to resize')
        this.scrollHandler()
      }
    }
  }

  /**
   * Set the current waypoint.
   * @param {Object} waypoint - The current waypoint/zone
   */
  setCurrentWaypoint(waypoint)
  {
    this.currentWaypoint = waypoint
  }

  /**
   * Update current waypoint based on scroll position.
   * @private
   */
  updateCurrentWaypointFromScroll()
  {
    if (!this.sceneManager.waypoints.length) return

    const scaledScrollOffset = this.scrollOffset * (this.sceneManager.waypoints.length - 1)
    const currentZoneIndex = Math.round(scaledScrollOffset)
    const waypoint = this.sceneManager.waypoints[ currentZoneIndex ]

    if (waypoint && waypoint !== this.currentWaypoint)
    {
      this.setCurrentWaypoint(waypoint)
    }
  }

  /**
   * Adjust camera position to keep current zone's content within the camera frustum.
   * Only runs when keepContentInFrame is enabled for the current waypoint.
   */
  keepContentInFrame()
  {
    if (this.busy) return
    if (!this.currentWaypoint) return
    if (!this.currentWaypoint.keepContentInFrame) return

    // Find the corresponding scene zone
    const sceneZone = this.sceneManager.getSceneZoneByIndex(this.currentWaypoint.index)
    if (!sceneZone) return

    // Dynamically calculate bounding box from scene objects in this zone
    const targetBox = this.calculateZoneBoundingBox(sceneZone)

    // Update visual debug box in debug mode
    if (isDebugMode)
    {
      this.showDebugBoundingBox(sceneZone, targetBox, true)
    }

    if (!targetBox || targetBox.isEmpty()) return

    // Get current camera position and target
    const currentPosition = new THREE.Vector3()
    this.controls.getPosition(currentPosition)

    const currentTarget = new THREE.Vector3()
    this.controls.getTarget(currentTarget)

    // Calculate required distance to fit content
    const requiredDistance = this.calculateDistanceToFitBox(
      targetBox,
      this.camera.fov,
      this.camera.aspect,
      1.5 // 50% padding
    )

    const currentDistance = currentPosition.distanceTo(currentTarget)

    // Only adjust if content is getting out of frame (camera too close)
    if (requiredDistance > currentDistance)
    {
      debugLog('[CameraManager] Keep-in-frame: adjusting camera distance from',
        currentDistance.toFixed(2), 'to', requiredDistance.toFixed(2))

      // Calculate new camera position along the current viewing direction
      const direction = new THREE.Vector3().subVectors(currentPosition, currentTarget).normalize()
      const newPosition = currentTarget.clone().add(direction.multiplyScalar(requiredDistance))

      // Smoothly move camera back
      this.controls.setLookAt(
        newPosition.x, newPosition.y, newPosition.z,
        currentTarget.x, currentTarget.y, currentTarget.z,
        true // Enable smooth transition
      )
    }
  }

  /**
   * Calculate the bounding box of all visible objects in a scene zone.
   * @param {Object} sceneZone - The scene zone
   * @returns {THREE.Box3} The bounding box of all zone objects
   */
  calculateZoneBoundingBox(sceneZone)
  {
    if (!this.sceneManager.scene) return new THREE.Box3()

    // Ensure world matrices are current for animated content
    this.sceneManager.scene.updateMatrixWorld(true)

    // Try to find objects with explicit zone assignment first
    const zoneBox = this.calculateBoxForZone(sceneZone.name)
    if (!zoneBox.isEmpty()) return zoneBox

    // Fall back to static target box if available
    if (sceneZone.camera?.target && !sceneZone.camera.target.isEmpty())
    {
      return sceneZone.camera.target
    }

    // Last resort: calculate from all visible meshes
    return this.calculateBoxForAllMeshes()
  }

  /**
   * Calculate bounding box for objects belonging to a specific zone.
   * @private
   */
  calculateBoxForZone(zoneName)
  {
    const box = new THREE.Box3()

    this.sceneManager.scene.traverse((object) =>
    {
      if (!object.isMesh || !object.visible) return

      const objectZone = this.getObjectZone(object)
      if (objectZone === zoneName)
      {
        this.expandBoxWithWorldGeometry(box, object)
      }
    })

    return box
  }

  /**
   * Get the zone name for an object, checking ancestors if needed.
   * @private
   */
  getObjectZone(object)
  {
    let zone = object.userData?.zone
    if (zone) return zone

    object.traverseAncestors((ancestor) =>
    {
      if (!zone && ancestor.userData?.zone)
      {
        zone = ancestor.userData.zone
      }
    })

    return zone
  }

  /**
   * Calculate bounding box from all visible meshes in scene.
   * @private
   */
  calculateBoxForAllMeshes()
  {
    const box = new THREE.Box3()

    this.sceneManager.scene.traverse((object) =>
    {
      if (!object.isMesh || !object.visible) return
      if (object.userData?.type === 'cameraAnchor') return
      if (object.name?.startsWith('_')) return

      this.expandBoxWithWorldGeometry(box, object)
    })

    return box
  }

  /**
   * Expand a bounding box using world-space geometry vertices.
   * This is more accurate than expandByObject for animated/skinned meshes.
   * @param {THREE.Box3} box - The box to expand
   * @param {THREE.Mesh} mesh - The mesh to include
   */
  expandBoxWithWorldGeometry(box, mesh)
  {
    if (!mesh.geometry)
    {
      // Fallback to expandByObject if no geometry
      box.expandByObject(mesh)
      return
    }

    const geometry = mesh.geometry

    // Compute bounding box and sphere (ensures fresh data)
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()

    // Transform local bounding box to world space
    const localBox = geometry.boundingBox.clone()
    const worldMatrix = mesh.matrixWorld

    // Apply world transform to the bounding box
    localBox.applyMatrix4(worldMatrix)

    // Expand the result box
    box.union(localBox)
  }

  /**
   * Create a debug wireframe box helper for visualizing bounding boxes.
   * @param {THREE.Color|number} color - The color for the wireframe
   * @param {string} name - Unique name for the helper object
   * @returns {THREE.LineSegments} The wireframe box helper
   */
  createDebugBox(color = 0x00ff00, name = 'debugBox')
  {
    const geometry = new THREE.BoxGeometry(1, 1, 1)
    const edges = new THREE.EdgesGeometry(geometry)
    const material = new THREE.LineBasicMaterial({
      color: color,
      linewidth: 2,
      transparent: true,
      opacity: 0.8
    })
    const wireframe = new THREE.LineSegments(edges, material)
    wireframe.name = `_CameraManager_${name}`
    wireframe.visible = false
    // Explicitly disable raycasting properties
    wireframe.raycast = () => { }
    wireframe.userData = { ignoreRaycast: true, isDebugBox: true }
    // Ensure renderOrder is high so it draws on top but doesn't affect raycast
    wireframe.renderOrder = 9999
    return wireframe
  }

  /**
   * Update debug box to match a bounding box.
   * @param {THREE.LineSegments} debugBox - The debug box mesh
   * @param {THREE.Box3} boundingBox - The bounding box to match
   */
  updateDebugBox(debugBox, boundingBox)
  {
    if (!debugBox || !boundingBox || boundingBox.isEmpty()) return

    const center = new THREE.Vector3()
    const size = new THREE.Vector3()
    boundingBox.getCenter(center)
    boundingBox.getSize(size)

    debugBox.position.copy(center)
    debugBox.scale.copy(size)
  }

  /**
   * Show visual debugging for the current zone's bounding box.
   * Creates or updates a wireframe box in the scene.
   * @param {Object} sceneZone - The scene zone to debug
   * @param {THREE.Box3} boundingBox - The bounding box to visualize
   * @param {boolean} isVisible - Whether to show the debug box
   */
  showDebugBoundingBox(sceneZone, boundingBox, isVisible = true)
  {
    if (!this.sceneManager?.scene) return

    // Skip default zones (internal/system zones)
    if (sceneZone.name?.startsWith('_default')) return

    // Skip fallback bounds
    if (boundingBox?.isFallback) return

    const boxName = `keepInFrame_${sceneZone.index || 0}`

    // Find or create the debug box
    let debugBox = this.sceneManager.scene.getObjectByName(`_CameraManager_${boxName}`)

    if (!debugBox)
    {
      // Determine color from zone
      let color = 0x00ff00 // Default green
      const zoneName = sceneZone.name

      // First check sceneZone.color (if ever populated)
      if (sceneZone.color)
      {
        color = new THREE.Color(sceneZone.color[ 0 ], sceneZone.color[ 1 ], sceneZone.color[ 2 ])
      }
      else if (sceneZone.camera?.anchor?.userData?.zoneColor)
      {
        const zc = sceneZone.camera.anchor.userData.zoneColor
        color = new THREE.Color(zc[ 0 ], zc[ 1 ], zc[ 2 ])
      }

      debugBox = this.createDebugBox(color, boxName)
      this.sceneManager.scene.add(debugBox)

      debugLog('[CameraManager] Created debug box for zone:', {
        zoneName: sceneZone.name,
        zoneIndex: sceneZone.index,
        color: color
      })
    }

    // Update the box position and size
    this.updateDebugBox(debugBox, boundingBox)
    debugBox.visible = isVisible

    // Log bounding box details
    if (boundingBox && !boundingBox.isEmpty())
    {
      const size = new THREE.Vector3()
      const center = new THREE.Vector3()
      boundingBox.getSize(size)
      boundingBox.getCenter(center)

      debugLog('[CameraManager] Bounding box details:', {
        zoneName: sceneZone.name,
        center: center.toArray().map(v => v.toFixed(2)),
        size: size.toArray().map(v => v.toFixed(2)),
        min: boundingBox.min.toArray().map(v => v.toFixed(2)),
        max: boundingBox.max.toArray().map(v => v.toFixed(2))
      })
    }
    else
    {
      debugLog('[CameraManager] Bounding box is empty for zone:', sceneZone.name)
    }
  }

  /**
   * Hide all debug bounding boxes.
   */
  hideAllDebugBoxes()
  {
    if (!this.sceneManager?.scene) return

    this.sceneManager.scene.traverse((object) =>
    {
      if (object.name?.startsWith('_CameraManager_keepInFrame_'))
      {
        object.visible = false
      }
    })
  }

  /**
   * Clean up resources (call when disposing the manager).
   */
  dispose()
  {
    // Remove resize listener
    window.removeEventListener('resize', this._handleResize)
    if (this._resizeTimeout) clearTimeout(this._resizeTimeout)
  }

  /**
   * Handle window resize events.
   * Triggers recalculation of adjusted positions for keepContentInFrame zones.
   * @private
   */
  /**
   * Handle window resize events.
   * Triggers recalculation of adjusted positions for keepContentInFrame zones.
   * @private
   */
  _handleResize()
  {
    if (this._resizeTimeout) clearTimeout(this._resizeTimeout)

    this._resizeTimeout = setTimeout(() =>
    {
      debugLog('[CameraManager] Window resized (debounced), marking adjusted positions for update')
      this.needsAdjustedPositionUpdate = true
    }, 2500)
  }

  /**
   * Recalculate adjusted anchor positions for all waypoints.
   * This should be called after resize or when content changes.
   * The adjusted positions account for keepContentInFrame fit-to-box calculations.
   */
  recalculateAdjustedPositions()
  {
    if (!this.sceneManager?.waypoints?.length) return

    debugLog('[CameraManager] Recalculating adjusted positions for', this.sceneManager.waypoints.length, 'waypoints')

    // Update world matrices before calculations
    this.sceneManager.scene?.updateMatrixWorld?.(true)

    this.sceneManager.waypoints.forEach((waypoint, idx) =>
    {
      const originalPosition = waypoint.camera.anchor?.position
      if (!originalPosition) return

      // Start with the original anchor position
      let adjustedPosition = originalPosition.clone()

      const target = waypoint.camera.targetPosition

      // If keepContentInFrame is enabled, calculate the adjusted position
      if (waypoint.keepContentInFrame)
      {
        // Get the corresponding scene zone for bounding box calculation
        const sceneZone = this.sceneManager.getSceneZoneByIndex(waypoint.index)
        let targetBox = null

        if (sceneZone)
        {
          targetBox = this.calculateZoneBoundingBox(sceneZone)

          // Visualize bounds in debug mode
          if (isDebugMode)
          {
            this.showDebugBoundingBox(sceneZone, targetBox, true)
          }
        }
        else
        {
          // Fallback if scene zone not linked (e.g. missing zone name in GLB)
          targetBox = waypoint.camera.target
          debugLog('[CameraManager] Waypoint', idx, 'missing scene zone, using waypoint target box')
        }

        if (targetBox && !targetBox.isEmpty())
        {
          const currentDist = originalPosition.distanceTo(target)
          const fittedDist = this.calculateDistanceToFitBox(
            targetBox,
            waypoint.camera.anchor.userData?.fov || waypoint.camera.anchor.fov,
            null, // Use current aspect ratio
            1.5 // 50% padding
          )

          debugLog('[CameraManager] Waypoint', idx, 'fit calc:', {
            zoneIndex: waypoint.index,
            currentDist: currentDist.toFixed(2),
            fittedDist: fittedDist.toFixed(2),
            fov: waypoint.camera.anchor.userData?.fov || waypoint.camera.anchor.fov,
            boxSize: (new THREE.Vector3().copy(targetBox.max).sub(targetBox.min)).toArray().map(v => v.toFixed(2))
          })

          // If content doesn't fit, move camera back
          if (fittedDist > currentDist)
          {
            const direction = new THREE.Vector3().subVectors(originalPosition, target).normalize()
            adjustedPosition = target.clone().add(direction.multiplyScalar(fittedDist))

            debugLog('[CameraManager] Waypoint', idx, 'adjusted distance from',
              currentDist.toFixed(2), 'to', fittedDist.toFixed(2))
          }
        }
      }

      // Store the adjusted position on the waypoint
      waypoint.camera.adjustedPosition = adjustedPosition

      debugLog('[CameraManager] Waypoint', idx, 'positions:', {
        zoneIndex: waypoint.index,
        original: originalPosition.toArray().map(v => v.toFixed(2)),
        adjusted: adjustedPosition.toArray().map(v => v.toFixed(2)),
        keepContentInFrame: waypoint.keepContentInFrame,
        targetPos: target.toArray().map(v => v.toFixed(2))
      })
    })

    this.needsAdjustedPositionUpdate = false
    debugLog('[CameraManager] Adjusted positions calculation complete')
  }

  /**
   * Get the adjusted position for a waypoint.
   * Falls back to original anchor position if no adjustment is needed/available.
   * @param {Object} waypoint - The waypoint
   * @returns {THREE.Vector3} The position to use for camera placement
   */
  getAdjustedPosition(waypoint)
  {
    // Recalculate if needed
    if (this.needsAdjustedPositionUpdate)
    {
      this.recalculateAdjustedPositions()
    }

    // Return adjusted position if available, otherwise original
    return waypoint.camera.adjustedPosition || waypoint.camera.anchor?.position
  }

  /**
   * Calculates the required camera distance to ensure all content inside
   * the scene zone is within the bounds of the camera frustum.
   * Uses the tangent of the camera's field of view to compute the optimal distance.
   * 
   * @param {Object} sceneZone - The scene zone to fit in the camera view
   * @param {number} padding - Optional padding multiplier (default 1.5 for 50% padding)
   * @param {boolean} enableTransition - Whether to animate the camera movement
   * @returns {Promise} Resolves when the camera has finished moving
   */
  fitCameraToSceneZone(sceneZone, padding = 1.5, enableTransition = true)
  {
    if (!sceneZone)
    {
      console.warn('CameraManager: No scene zone provided to fitCameraToSceneZone')
      return Promise.resolve()
    }

    const targetBox = sceneZone.camera?.target
    if (!targetBox || targetBox.isEmpty())
    {
      console.warn('CameraManager: Scene zone has no valid bounding box')
      return Promise.resolve()
    }

    // Get the bounding box size and center
    const boxSize = new THREE.Vector3()
    targetBox.getSize(boxSize)

    const boxCenter = new THREE.Vector3()
    targetBox.getCenter(boxCenter)

    // Get camera properties
    const camera = this.controls.camera
    const fovRadians = THREE.MathUtils.degToRad(camera.fov)
    const aspect = camera.aspect

    // Calculate the required distance for vertical FOV
    // distance = (height / 2) / tan(fov / 2)
    const halfFovV = fovRadians / 2
    const distanceForHeight = (boxSize.y / 2) / Math.tan(halfFovV)

    // Calculate the required distance for horizontal FOV
    // Horizontal FOV = 2 * atan(tan(fov/2) * aspect)
    const halfFovH = Math.atan(Math.tan(halfFovV) * aspect)
    const distanceForWidth = (boxSize.x / 2) / Math.tan(halfFovH)

    // Also consider depth - camera should be at least depth/2 away from near edge
    const distanceForDepth = boxSize.z / 2

    // Take the maximum distance needed to fit all dimensions
    let requiredDistance = Math.max(distanceForHeight, distanceForWidth, distanceForDepth)

    // Apply padding
    requiredDistance *= padding

    // Get the current camera direction (from camera position to target)
    const cameraPosition = sceneZone.camera.anchor?.position
    if (!cameraPosition)
    {
      console.warn('CameraManager: Scene zone has no camera anchor position')
      return Promise.resolve()
    }

    const targetPosition = sceneZone.camera.targetPosition

    // Calculate the direction from target to camera (normalized)
    const direction = new THREE.Vector3()
    direction.subVectors(cameraPosition, targetPosition).normalize()

    // Calculate the new camera position
    const newCameraPosition = new THREE.Vector3()
    newCameraPosition.copy(targetPosition).add(direction.multiplyScalar(requiredDistance))

    // Move the camera to the new position
    if (this.controls)
    {
      return this.controls.setLookAt(
        newCameraPosition.x,
        newCameraPosition.y,
        newCameraPosition.z,
        targetPosition.x,
        targetPosition.y,
        targetPosition.z,
        enableTransition
      )
    }

    return Promise.resolve()
  }

  /**
   * Fits the camera to the currently active scene zone (based on scroll position).
   * 
   * @param {number} padding - Optional padding multiplier (default 1.5)
   * @param {boolean} enableTransition - Whether to animate the camera movement
   * @returns {Promise} Resolves when the camera has finished moving
   */
  fitCameraToCurrentSceneZone(padding = 1.5, enableTransition = true)
  {
    // Calculate current zone index from scroll offset
    const scaledScrollOffset = this.scrollOffset * (this.sceneManager.waypoints.length - 1)
    const currentZoneIndex = Math.round(scaledScrollOffset)
    const currentZone = this.sceneManager.getSceneZoneByIndex(currentZoneIndex)

    if (!currentZone)
    {
      console.warn('CameraManager: Could not find current scene zone')
      return Promise.resolve()
    }

    return this.fitCameraToSceneZone(currentZone, padding, enableTransition)
  }

  /**
   * Calculates the optimal camera distance for a given bounding box and FOV.
   * This is a utility method that can be called externally for custom calculations.
   * 
   * @param {THREE.Box3} boundingBox - The bounding box to fit
   * @param {number} fov - Field of view in degrees
   * @param {number} aspect - Camera aspect ratio
   * @param {number} padding - Optional padding multiplier (default 1.0)
   * @returns {number} The required camera distance
   */
  calculateDistanceToFitBox(boundingBox, fov = null, aspect = null, padding = 1.0)
  {
    const camera = this.controls.camera
    const fovDeg = fov ?? camera.fov
    const aspectRatio = aspect ?? camera.aspect
    const fovRadians = THREE.MathUtils.degToRad(fovDeg)

    const boxSize = new THREE.Vector3()
    boundingBox.getSize(boxSize)

    // Calculate distances for both vertical and horizontal FOV
    const halfFovV = fovRadians / 2
    const distanceForHeight = (boxSize.y / 2) / Math.tan(halfFovV)

    const halfFovH = Math.atan(Math.tan(halfFovV) * aspectRatio)
    const distanceForWidth = (boxSize.x / 2) / Math.tan(halfFovH)

    const distanceForDepth = boxSize.z / 2

    return Math.max(distanceForHeight, distanceForWidth, distanceForDepth) * padding
  }

  /**
   * Performs a raycast from the camera through a screen position to find intersections.
   * @param {THREE.Vector2} mouse - Normalized mouse coordinates (-1 to 1)
   * @param {Array} objects - Array of objects to test for intersection (optional, defaults to scene children)
   * @returns {Array} Array of intersection results
   */
  raycastFromMouse(mouse, objects = null)
  {
    if (!this.camera || !this.sceneManager?.scene) return []

    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(mouse, this.camera)

    const intersectObjects = objects || this.sceneManager.scene.children
    return raycaster.intersectObjects(intersectObjects, true)
  }

  /**
   * Moves the camera target to a specific 3D point with smooth animation.
   * @param {THREE.Vector3} targetPoint - The point to move the camera target to
   * @param {boolean} animate - Whether to animate the movement (default: true)
   * @returns {Promise} Promise that resolves when the movement is complete
   */
  moveCameraTargetTo(targetPoint, animate = true)
  {
    if (!this.controls || this.busy) return Promise.resolve()

    debugLog('[CameraManager] Moving camera target to:', targetPoint.toArray())

    this.busy = true

    const currentPosition = new THREE.Vector3()
    this.controls.getPosition(currentPosition)

    const currentTarget = new THREE.Vector3()
    this.controls.getTarget(currentTarget)

    if (animate)
    {
      return this.controls.setLookAt(currentPosition.x, currentPosition.y, currentPosition.z,
        targetPoint.x, targetPoint.y, targetPoint.z, true)
        .then(() =>
        {
          debugLog('[CameraManager] Camera target movement completed')
          this.busy = false
        })
        .catch((error) =>
        {
          debugLog('[CameraManager] Camera target movement failed:', error)
          this.busy = false
        })
    } else
    {
      this.controls.setLookAt(currentPosition.x, currentPosition.y, currentPosition.z,
        targetPoint.x, targetPoint.y, targetPoint.z, false)
      this.busy = false
      return Promise.resolve()
    }
  }

  /**
   * Handles double-click interaction by raycasting and moving camera target.
   * @param {THREE.Vector2} mouse - Normalized mouse coordinates (-1 to 1)
   */
  handleDoubleClick(mouse)
  {
    const intersections = this.raycastFromMouse(mouse)

    if (intersections.length > 0)
    {
      const hitPoint = intersections[ 0 ].point
      debugLog('[CameraManager] Double-click hit at:', hitPoint.toArray())
      this.moveCameraTargetTo(hitPoint)
    }
  }

}
